import { ScenarioCageData } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-cage-data.type';
import { FishGroupSummary } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';
import { MetricValueCategory } from 'views/bluviu-nutrition/features/scenarios-v2/utils/format-metric-value';

export type StatRow = {
  label: string;
  refValue: number | null | undefined;
  overrideValue: number | null | undefined;
  valueKey?: string;
  category?: MetricValueCategory;
  delta: number | null;
};

export interface FishGroupValidationState {
  isMeanWeightInvalid: boolean;
  isStockingDateInvalid: boolean;
  isFishGroupStartDateInvalid: boolean;
  isScenarioEndDateInvalid: boolean;
  isScenarioDateRangeInvalid: boolean;
  isInvalid: boolean;
}

type FishGroupValidationInput = Pick<ScenarioCageData, 'meanWeight' | 'stockingDate' | 'startDate'>;

const MIN_MEAN_WEIGHT = 50;
const MAX_MEAN_WEIGHT = 3000;

const getDateTime = (date: string | undefined): number => new Date(date ?? '').getTime();

const isFishGroupMeanWeightInvalid = ({ meanWeight }: FishGroupValidationInput): boolean =>
  meanWeight > MAX_MEAN_WEIGHT || meanWeight < MIN_MEAN_WEIGHT;

const isFishGroupStockingDateInvalid = ({ stockingDate, startDate }: FishGroupValidationInput): boolean => {
  const stockingDateTime = getDateTime(stockingDate);
  const startDateTime = getDateTime(startDate);

  return Number.isNaN(stockingDateTime) || (!Number.isNaN(startDateTime) && stockingDateTime > startDateTime);
};

const hasInvalidFishGroupStartDate = ({ startDate }: FishGroupValidationInput): boolean => Number.isNaN(getDateTime(startDate));

const hasInvalidScenarioEndDate = (scenarioEndDate: string | undefined): boolean => Number.isNaN(getDateTime(scenarioEndDate));

const hasInvalidScenarioDateRange = ({ startDate }: FishGroupValidationInput, scenarioEndDate: string | undefined): boolean => {
  const startDateTime = getDateTime(startDate);
  const scenarioEndDateTime = getDateTime(scenarioEndDate);

  return !Number.isNaN(startDateTime) && !Number.isNaN(scenarioEndDateTime) && startDateTime >= scenarioEndDateTime;
};

/**
 * Centralizes cage data rules used before a fish group can participate in a scenario run.
 * Callers keep inclusion checks and localized messages so unchecked cages can stay visually quiet.
 */
export const getFishGroupValidationState = (
  cage: FishGroupValidationInput,
  scenarioEndDate: string | undefined
): FishGroupValidationState => {
  const isMeanWeightInvalid = isFishGroupMeanWeightInvalid(cage);
  const isStockingDateInvalid = isFishGroupStockingDateInvalid(cage);
  const isFishGroupStartDateInvalid = hasInvalidFishGroupStartDate(cage);
  const isScenarioEndDateInvalid = hasInvalidScenarioEndDate(scenarioEndDate);
  const isScenarioDateRangeInvalid = hasInvalidScenarioDateRange(cage, scenarioEndDate);

  return {
    isMeanWeightInvalid,
    isStockingDateInvalid,
    isFishGroupStartDateInvalid,
    isScenarioEndDateInvalid,
    isScenarioDateRangeInvalid,
    isInvalid:
      isMeanWeightInvalid ||
      isStockingDateInvalid ||
      isFishGroupStartDateInvalid ||
      isScenarioEndDateInvalid ||
      isScenarioDateRangeInvalid,
  };
};

const getDelta = (ref: number | undefined | null, override: number | undefined | null): number | null => {
  if (ref == null || override == null) return null;
  return override - ref;
};

export const buildFishGroupStats = (ref: FishGroupSummary, override?: FishGroupSummary): StatRow[] => [
  {
    label: 'Final weight',
    refValue: ref.finalWeightGrams,
    overrideValue: override?.finalWeightGrams,
    valueKey: 'finalWeightGrams',
    delta: getDelta(ref.finalWeightGrams, override?.finalWeightGrams),
  },
  {
    label: 'Days to reach',
    refValue: ref.daysToReach,
    overrideValue: override?.daysToReach,
    valueKey: 'daysToReach',
    delta: getDelta(ref.daysToReach, override?.daysToReach),
  },
  {
    label: 'bFCR',
    refValue: ref.bFcr,
    overrideValue: override?.bFcr,
    valueKey: 'bFcr',
    delta: getDelta(ref.bFcr, override?.bFcr),
  },
  {
    label: 'Cost of HOG gain',
    refValue: ref.feedCostOfHogGain,
    overrideValue: override?.feedCostOfHogGain,
    valueKey: 'feedCostOfHogGain',
    delta: getDelta(ref.feedCostOfHogGain, override?.feedCostOfHogGain),
  },
  {
    label: 'GWP CO₂/kg growth',
    refValue: ref.globalWarmingPotentialCo2KgPerKgGrowth,
    overrideValue: override?.globalWarmingPotentialCo2KgPerKgGrowth,
    valueKey: 'globalWarmingPotentialCo2KgPerKgGrowth',
    delta: getDelta(ref.globalWarmingPotentialCo2KgPerKgGrowth, override?.globalWarmingPotentialCo2KgPerKgGrowth),
  },
  {
    label: 'Weight gain',
    refValue: ref.weightGainGrams,
    overrideValue: override?.weightGainGrams,
    valueKey: 'weightGainGrams',
    delta: getDelta(ref.weightGainGrams, override?.weightGainGrams),
  },
  {
    label: 'Feed Intake',
    refValue: ref.feedIntakeGrams,
    overrideValue: override?.feedIntakeGrams,
    valueKey: 'feedIntakeGrams',
    delta: getDelta(ref.feedIntakeGrams, override?.feedIntakeGrams),
  },
  {
    label: 'Carcass Yield',
    refValue: ref.carcassYieldPercent,
    overrideValue: override?.carcassYieldPercent,
    valueKey: 'carcassYieldPercent',
    delta: getDelta(ref.carcassYieldPercent, override?.carcassYieldPercent),
  },
  {
    label: 'Fillet Fat',
    refValue: ref.filletFatPercent,
    overrideValue: override?.filletFatPercent,
    valueKey: 'filletFatPercent',
    delta: getDelta(ref.filletFatPercent, override?.filletFatPercent),
  },
  {
    label: 'Avg. Cost Of Growth',
    refValue: ref.feedCostOfGrowth,
    overrideValue: override?.feedCostOfGrowth,
    valueKey: 'feedCostOfGrowth',
    delta: getDelta(ref.feedCostOfGrowth, override?.feedCostOfGrowth),
  },
  {
    label: 'HOG Gain',
    refValue: ref.hogGainGrams,
    overrideValue: override?.hogGainGrams,
    valueKey: 'hogGainGrams',
    delta: getDelta(ref.hogGainGrams, override?.hogGainGrams),
  },
  {
    label: 'GWP CO₂/kg feed',
    refValue: ref.globalWarmingPotentialCo2KgPerKgFeed,
    overrideValue: override?.globalWarmingPotentialCo2KgPerKgFeed,
    valueKey: 'globalWarmingPotentialCo2KgPerKgFeed',
    delta: getDelta(ref.globalWarmingPotentialCo2KgPerKgFeed, override?.globalWarmingPotentialCo2KgPerKgFeed),
  },
  {
    label: 'eFCR',
    refValue: ref.eFcr,
    overrideValue: override?.eFcr,
    valueKey: 'eFcr',
    delta: getDelta(ref.eFcr, override?.eFcr),
  },
  {
    label: 'Potential sales value',
    refValue: ref.potentialSalesValue,
    overrideValue: override?.potentialSalesValue,
    valueKey: 'potentialSalesValue',
    delta: getDelta(ref.potentialSalesValue, override?.potentialSalesValue),
  },
  {
    label: 'Total feed cost',
    refValue: ref.costBreakdown?.feedCostTotal,
    overrideValue: override?.costBreakdown?.feedCostTotal,
    category: 'currency',
    delta: getDelta(ref.costBreakdown?.feedCostTotal, override?.costBreakdown?.feedCostTotal),
  },
  {
    label: 'Total smolt cost for all',
    refValue: ref.costBreakdown?.smoltCostTotal,
    overrideValue: override?.costBreakdown?.smoltCostTotal,
    category: 'currency',
    delta: getDelta(ref.costBreakdown?.smoltCostTotal, override?.costBreakdown?.smoltCostTotal),
  },
  {
    label: 'Total operation cost',
    refValue: ref.costBreakdown?.operationsCostTotal,
    overrideValue: override?.costBreakdown?.operationsCostTotal,
    category: 'currency',
    delta: getDelta(ref.costBreakdown?.operationsCostTotal, override?.costBreakdown?.operationsCostTotal),
  },
  {
    label: 'Total sealice cost',
    refValue: ref.costBreakdown?.treatmentCostTotal,
    overrideValue: override?.costBreakdown?.treatmentCostTotal,
    category: 'currency',
    delta: getDelta(ref.costBreakdown?.treatmentCostTotal, override?.costBreakdown?.treatmentCostTotal),
  },
  {
    label: 'Total harvest cost',
    refValue: ref.costBreakdown?.harvestCostTotal,
    overrideValue: override?.costBreakdown?.harvestCostTotal,
    category: 'currency',
    delta: getDelta(ref.costBreakdown?.harvestCostTotal, override?.costBreakdown?.harvestCostTotal),
  },
  {
    label: 'Total cost',
    refValue: ref.totalCost,
    overrideValue: override?.totalCost,
    valueKey: 'totalCost',
    delta: getDelta(ref.totalCost, override?.totalCost),
  },
];

export const selectFishGroupsByProgram = (summaries: FishGroupSummary[], selectedProgram: string): FishGroupSummary[] => {
  const grouped = summaries.reduce<Map<string, FishGroupSummary[]>>((acc, summary) => {
    const current = acc.get(summary.fishGroupId) ?? [];
    acc.set(summary.fishGroupId, [...current, summary]);
    return acc;
  }, new Map());

  return Array.from(grouped.values()).map((items) =>
    selectedProgram ? (items.find((item) => item.feedPlan === selectedProgram) ?? items[0]) : items[0]
  );
};
